import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Button,
  Grid,
  Typography,
  TextField,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { Breadcrumb, BackdropCircular, ConfirmDialog} from 'components';
import {
  getCatalogStoreCodesAPI,
  getStoresAPI,
  saveEditedStoreAPI,
  getTierSubscriptionPlansApi,
  getStoreTypeApi
} from 'api';
import { storeTypeNameUI } from 'helpers/storeTypeNames';


const storeValidationSchema = yup
  .object()
  .shape({
    quoteProStoreId: yup.number().when(['quoteProStoreCode', 'quoteProCustomerUrl', 'checkoutUrl'], {
      is: (quoteProStoreCode, quoteProCustomerUrl, checkoutUrl) =>
        quoteProStoreCode !== undefined || quoteProCustomerUrl !== undefined || checkoutUrl !== undefined,
      then: yup.number().required('Field is required')
    }),
    quoteProStoreCode: yup.string().when(['quoteProStoreId', 'quoteProCustomerUrl', 'checkoutUrl'], {
      is: (quoteProStoreId, quoteProCustomerUrl, checkoutUrl) =>
        quoteProStoreId !== undefined || quoteProCustomerUrl !== undefined || checkoutUrl !== undefined,
      then: yup.string().required('Field is required')
    }),
    quoteProCustomerUrl: yup.string().when(['quoteProStoreId', 'quoteProStoreCode', 'checkoutUrl'], {
      is: (quoteProStoreId, quoteProStoreCode, checkoutUrl) =>
        quoteProStoreId !== undefined || quoteProStoreCode !== undefined || checkoutUrl !== undefined,
      then: yup.string().required('Field is required')
    }),
    checkoutUrl: yup.string().when(['quoteProStoreId', 'quoteProStoreCode', 'quoteProCustomerUrl'], {
      is: (quoteProStoreId, quoteProStoreCode, quoteProCustomerUrl) =>
        quoteProStoreId !== undefined || quoteProStoreCode !== undefined || quoteProCustomerUrl !== undefined,
      then: yup.string().required('Field is required')
    }),
    oneloginAppId: yup.number().required('Field is required'),
  }, [
    ['quoteProStoreId', 'quoteProStoreCode'],
    ['quoteProStoreId', 'quoteProCustomerUrl'],
    ['quoteProStoreId', 'checkoutUrl'],
    ['quoteProStoreCode', 'quoteProCustomerUrl'],
    ['quoteProStoreCode', 'checkoutUrl'],
    ['quoteProCustomerUrl', 'checkoutUrl']
  ]);


const EditStore = () => {
  const dispatch = useDispatch();
  const params = useParams<{id?: string}>();
  const history = useHistory();
  const {
    stores,
    loading,
    error,
    catalogStoreCodes,
    magentoStoreType,
    loadingMagentoStoreType
  } = useSelector((state: IReduxState) => state.admin);
  const { subscriptionTiers } = useSelector((state: IReduxState) => state.user);
  const store = stores?.find((store) => params.id == store._id);
  const storeTier = subscriptionTiers?.find((tier) => tier._id === store?.subscriptionTier);
  const storeTierPlans = storeTier?.plans || [];

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  useEffect(() => {
    getStoresAPI({dispatch});
    getCatalogStoreCodesAPI({dispatch});
    getTierSubscriptionPlansApi({dispatch});
    getStoreTypeApi({dispatch});
  }, []);

  const handleSubmit = async (values) => {
    for (const key in values) {
      if (typeof values[key] === 'string') {
        values[key] = values[key].trim();
      }
    }

    await saveEditedStoreAPI(dispatch, values);
    history.push('/admin');
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y px-10 pb-25">
      {!loading && !error &&
      <>
        <div className="mb-sm-30">
          <Breadcrumb
            routeSegments={[
              { name: 'Admin Panel', path: '/admin' },
              { name: `${store?.storeCode}` }
            ]}
          />
        </div>
        <Grid container spacing={5}>
          <Formik
            initialValues={{
              _id: store?._id || null,
              storeId: store?.storeId || null,
              apiUrl: store?.apiUrl || null,
              storeUrl: store?.storeUrl || null,
              storeCode: store?.storeCode || null,
              accessToken: store?.accessToken || null,
              touchmdApiKey: store?.touchmdApiKey || null,
              quoteProStoreId: store?.quoteProStoreId || '',
              quoteProStoreCode: store?.quoteProStoreCode || '',
              oneloginAppId: store?.oneloginAppId || null,
              quoteProCustomerUrl: store?.quoteProCustomerUrl || '',
              checkoutUrl: store?.checkoutUrl || '',
              alleInsightUrl: store?.alleInsightUrl || null,
              alleInsightApiKey: store?.alleInsightApiKey || null,
              rpaUrl: store?.rpaUrl || null,
              rpaApiKey: store?.rpaApiKey || null,
              touchmdServiceUrl: store?.touchmdServiceUrl || null,
              touchmdServiceApiKey: store?.touchmdServiceApiKey || null,
              aspireServiceUrl: store?.aspireServiceUrl || null,
              aspireServiceApiKey: store?.aspireServiceApiKey || null,
              modmedServiceUrl: store?.modmedServiceUrl || null,
              modmedServiceApiKey: store?.modmedServiceApiKey || null,
              ghlLocationId: store?.ghlLocationId || null,
              ghlApiKey: store?.ghlApiKey || null,
              aestheticKpiWarehouseUrl: store?.aestheticKpiWarehouseUrl || null,
              aestheticKpiWarehouseApiKey: store?.aestheticKpiWarehouseApiKey || null,
              nextechUsername: store?.nextechUsername || null,
              nextechPassword: store?.nextechPassword || null,
              nextechClientId: store?.nextechClientId || null,
              nextechPracticeId: store?.nextechPracticeId || null,
              storeType: store?.storeType || null,
              catalogStoreCode: store?.catalogStoreCode || null,
              isCreated: store?.isCreated || false,
              isLive: store?.isLive || false,
              isOldMigrated: store?.isOldMigrated || false,
              subscriptionTier: store?.subscriptionTier || null,
              subscriptionId: store?.subscriptionId || null,
            }}
            validationSchema={storeValidationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit
            }) => (
              <form noValidate autoComplete="off" className="p-8 w-full">
                <Grid item xs={11}>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Store Id:</Typography>
                    <TextField
                      variant="outlined"
                      type="number"
                      name="storeId"
                      fullWidth
                      value={values.storeId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Api Url:</Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="apiUrl"
                      fullWidth
                      value={values.apiUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Store Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="storeUrl"
                      fullWidth
                      value={values.storeUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Store Code: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="storeCode"
                      fullWidth
                      value={values.storeCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Access Token: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="accessToken"
                      fullWidth
                      value={values.accessToken}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Touchmd Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="touchmdApiKey"
                      fullWidth
                      value={values.touchmdApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Quote Pro Store ID :</Typography>
                    <TextField
                      variant="outlined"
                      type="number"
                      name="quoteProStoreId"
                      fullWidth
                      value={values.quoteProStoreId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.quoteProStoreId && Boolean(errors.quoteProStoreId)}
                      helperText={touched.quoteProStoreId && errors.quoteProStoreId}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Quote Pro Store Code: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="quoteProStoreCode"
                      fullWidth
                      value={values.quoteProStoreCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.quoteProStoreCode && Boolean(errors.quoteProStoreCode)}
                      helperText={touched.quoteProStoreCode && errors.quoteProStoreCode}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">QuotePro Customer Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="quoteProCustomerUrl"
                      fullWidth
                      value={values.quoteProCustomerUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.quoteProCustomerUrl && Boolean(errors.quoteProCustomerUrl)}
                      helperText={touched.quoteProCustomerUrl && errors.quoteProCustomerUrl}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Checkout Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="checkoutUrl"
                      fullWidth
                      value={values.checkoutUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.checkoutUrl && Boolean(errors.checkoutUrl)}
                      helperText={touched.checkoutUrl && errors.checkoutUrl}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Onelogin App Id :</Typography>
                    <TextField
                      variant="outlined"
                      type="number"
                      name="oneloginAppId"
                      fullWidth
                      value={values.oneloginAppId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.oneloginAppId && Boolean(errors.oneloginAppId)}
                      helperText={touched.oneloginAppId && errors.oneloginAppId}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Alle Service Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="alleInsightUrl"
                      fullWidth
                      value={values.alleInsightUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Alle Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="alleInsightApiKey"
                      fullWidth
                      value={values.alleInsightApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">RPA Service Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="rpaUrl"
                      fullWidth
                      value={values.rpaUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">RPA Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="rpaApiKey"
                      fullWidth
                      value={values.rpaApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">TOUCHMD Service Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="touchmdServiceUrl"
                      fullWidth
                      value={values.touchmdServiceUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">TOUCHMD Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="touchmdServiceApiKey"
                      fullWidth
                      value={values.touchmdServiceApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">ASPIRE Service Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="aspireServiceUrl"
                      fullWidth
                      value={values.aspireServiceUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">ASPIRE Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="aspireServiceApiKey"
                      fullWidth
                      value={values.aspireServiceApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">MODMED Service Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="modmedServiceUrl"
                      fullWidth
                      value={values.modmedServiceUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">MODMED Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="modmedServiceApiKey"
                      fullWidth
                      value={values.modmedServiceApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">GHL Location Id: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="ghlLocationId"
                      fullWidth
                      value={values.ghlLocationId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">GHL Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="ghlApiKey"
                      fullWidth
                      value={values.ghlApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Aesthetic Kpi Warehouse Url: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="aestheticKpiWarehouseUrl"
                      fullWidth
                      value={values.aestheticKpiWarehouseUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Aesthetic Kpi Warehouse Api Key: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="aestheticKpiWarehouseApiKey"
                      fullWidth
                      value={values.aestheticKpiWarehouseApiKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Nextech Username: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="nextechUsername"
                      fullWidth
                      value={values.nextechUsername}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Nextech Password: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="nextechPassword"
                      fullWidth
                      value={values.nextechPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Client ID: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="nextechClientId"
                      fullWidth
                      value={values.nextechClientId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Nextech Practice Id: </Typography>
                    <TextField
                      variant="outlined"
                      type="text"
                      name="nextechPracticeId"
                      fullWidth
                      value={values.nextechPracticeId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Store Type: </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Store Type ({magentoStoreType} in magento):</InputLabel>
                      <Select
                        name="storeType"
                        label="Store Type"
                        value={values.storeType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        {Object.keys(storeTypeNameUI).map((type) => (
                          <MenuItem
                            key={`storeType-${type}`}
                            value={type}
                          >
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className=" pt-2 font-bold">Catalog store code:</Typography>
                    <FormControl fullWidth>
                      <InputLabel >Catalog store code:</InputLabel>
                      <Select
                        name="catalogStoreCode"
                        label="Catalog Store Code"
                        value={values.catalogStoreCode}
                        onChange={handleChange}
                      >
                        {catalogStoreCodes.map((storeCode) => (
                          <MenuItem key={storeCode} value={storeCode} >{storeCode}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="pt-4 font-bold">Created:
                    <Switch
                      checked={!!values?.isCreated}
                      color="primary"
                      name="isCreated"
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      className="ml-2"
                      onChange={handleChange}
                    />
                  </div>
                  <div className="pt-4 font-bold">Is Live:
                    <Switch
                      checked={!!values.isLive}
                      color="primary"
                      name="isLive"
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      className="ml-2"
                      onChange={handleChange}
                    />
                  </div>
                  <div className="pt-4 font-bold">Old migrated:
                    <Switch
                      checked={!!values.isOldMigrated}
                      color="primary"
                      name="isOldMigrated"
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      className="ml-2"
                      onChange={handleChange}
                    />
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className="pt-2 font-bold">Subscription Tier: </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Subscription Tier:</InputLabel>
                      <Select
                        name="subscriptionTier"
                        label="Subscription Tier:"
                        value={values.subscriptionTier}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        {subscriptionTiers.map((tier) => <MenuItem value={tier._id} key={tier._id}>{tier.title}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="py-4 flex items-stretch">
                    <Typography variant="body1" className="pt-2 font-bold">Subscription Plan: </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Subscription Plan:</InputLabel>
                      <Select
                        name="subscriptionId"
                        label="Subscription Plan:"
                        value={values.subscriptionId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.subscriptionId && Boolean(errors.subscriptionId)}
                      >
                        {storeTierPlans.map((plan) => <MenuItem value={plan._id} key={plan._id}>{plan.title}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </div>
                </Grid>
                <Grid item xs={12} className="text-right">
                  <Button
                    onClick={() => history.push('/admin')}
                    className="mr-5"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenConfirmDialog(true)}
                  >
                    Save Changes
                  </Button>
                  <ConfirmDialog
                    title="Confirm Changes?"
                    open={openConfirmDialog}
                    setOpen={() => setOpenConfirmDialog(false)}
                    onConfirm={() => handleSubmit(values)}
                  >
                    Are you sure you want to change configuration for <b>{store?.storeCode}</b> Store on the Admin page?
                  </ConfirmDialog>
                </Grid>
              </form>
            )
            }
          </Formik>
        </Grid>
      </>}
      <BackdropCircular loading={loading || loadingMagentoStoreType} />
    </div>
  );
};


export default EditStore;
